import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Store, Video, Globe, Phone } from 'lucide-react';
import { API } from '../../App';
import AutoTranslatedText from '../AutoTranslatedText';
import OptimizedImage from '../OptimizedImage';

const BuyerBoothDetailModal = ({
  open, onOpenChange, selectedBooth, boothDetailTab, setBoothDetailTab,
  onTalkToRep, formatPrice, t,
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setBoothDetailTab('overview'); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {selectedBooth?.logo_url ? (
                  <OptimizedImage src={selectedBooth.logo_url} alt={selectedBooth?.company_name} className="max-w-full max-h-full" objectFit="contain" />
                ) : (
                  <Store className="h-8 w-8 text-slate-400" />
                )}
              </div>
              <div>
                <DialogTitle className="text-xl">{selectedBooth?.company_name}</DialogTitle>
                {selectedBooth?.slogan && <DialogDescription><AutoTranslatedText text={selectedBooth.slogan} translations={selectedBooth.slogan_translations} showLoading={false} /></DialogDescription>}
              </div>
            </div>
            <Badge className={
              selectedBooth?.tier === 'elite' ? 'bg-purple-100 text-purple-700 border-2 border-purple-400 ring-2 ring-purple-200' :
              selectedBooth?.tier === 'premium' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
              'bg-slate-100 text-slate-700'
            }>
              {selectedBooth?.tier === 'elite' ? t('event_detail.tier_elite') : selectedBooth?.tier === 'premium' ? t('event_detail.tier_premium') : t('event_detail.tier_basic')}
            </Badge>
          </div>
        </DialogHeader>
        {selectedBooth && (
          <Tabs value={boothDetailTab} onValueChange={setBoothDetailTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">{t('dashboard.booth_overview')}</TabsTrigger>
              <TabsTrigger value="gallery">{t('dashboard.booth_gallery_videos')}</TabsTrigger>
              <TabsTrigger value="products">{t('dashboard.booth_products_tab')}</TabsTrigger>
            </TabsList>
            <div className="flex-1 overflow-y-auto mt-4">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0 space-y-4">
                {selectedBooth.banner_url && (
                  <div className="aspect-[3/1] rounded-lg overflow-hidden">
                    <OptimizedImage src={selectedBooth.banner_url} alt="Banner" className="w-full h-full" objectFit="cover" />
                  </div>
                )}
                <p className="text-slate-600"><AutoTranslatedText text={selectedBooth.description} translations={selectedBooth.description_translations} showLoading={false} /></p>
                {selectedBooth.seller?.company_info && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">{t('dashboard.company_information')}</h4>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><strong>{t('dashboard.industry_label')}</strong> {selectedBooth.seller.company_info.industry}</p>
                      <p><strong>{t('dashboard.location_label')}</strong> {selectedBooth.seller.company_info.city}, {selectedBooth.seller.company_info.country}</p>
                      {selectedBooth.seller.company_info.website && <p><Globe className="inline h-4 w-4 mr-1" /><a href={selectedBooth.seller.company_info.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{selectedBooth.seller.company_info.website}</a></p>}
                      <p><Phone className="inline h-4 w-4 mr-1" />{selectedBooth.seller.company_info.phone}</p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Gallery & Videos Tab */}
              <TabsContent value="gallery" className="mt-0 space-y-6">
                <div>
                  <h4 className="font-medium mb-3">{t('dashboard.gallery_title')}</h4>
                  {(selectedBooth.media || []).filter(m => m.type === 'image').length === 0 ? (
                    <p className="text-sm text-slate-500">{t('dashboard.no_gallery_images')}</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(selectedBooth.media || []).filter(m => m.type === 'image').map((media, i) => (
                        <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                          <img src={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} alt="Gallery" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium mb-3">{t('dashboard.videos_title')}</h4>
                  {(selectedBooth.media || []).filter(m => m.type === 'video').length === 0 ? (
                    <p className="text-sm text-slate-500">{t('dashboard.no_videos')}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {(selectedBooth.media || []).filter(m => m.type === 'video').map((media, i) => (
                        <div key={i} className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
                          <video src={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} className="w-full h-full object-contain" controls />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium mb-3">{t('dashboard.catalogs_brochures')}</h4>
                  {(selectedBooth.media || []).filter(m => m.type === 'pdf').length === 0 ? (
                    <p className="text-sm text-slate-500">{t('dashboard.no_documents')}</p>
                  ) : (
                    <div className="space-y-2">
                      {(selectedBooth.media || []).filter(m => m.type === 'pdf').map((media, i) => (
                        <a key={i} href={media.url.startsWith('/api') ? `${API}${media.url.replace('/api', '')}` : media.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                          <div className="w-10 h-12 bg-red-50 border border-red-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/></svg>
                          </div>
                          <div><p className="font-medium text-sm">{media.filename || 'Document.pdf'}</p><p className="text-xs text-slate-500">{t('dashboard.click_to_view')}</p></div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Products Tab */}
              <TabsContent value="products" className="mt-0">
                <h4 className="font-medium mb-3">{t('dashboard.products_services_title')}</h4>
                {selectedBooth.products?.length === 0 ? (
                  <p className="text-sm text-slate-500">{t('dashboard.no_products')}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {selectedBooth.products?.map((product, i) => (
                      <Card key={i} className="overflow-hidden">
                        <div className="aspect-square bg-slate-100 flex items-center justify-center">
                          {product.image_url ? (
                            <img src={product.image_url.startsWith('/api') ? `${API}${product.image_url.replace('/api', '')}` : product.image_url} alt={product.name} className="max-w-full max-h-full object-contain" />
                          ) : (
                            <Store className="h-8 w-8 text-slate-300" />
                          )}
                        </div>
                        <CardContent className="p-3">
                          <h5 className="font-medium">{product.name}</h5>
                          <p className="text-sm text-slate-600 line-clamp-2">{product.description}</p>
                          {product.price && <p className="text-lg font-semibold mt-1 text-primary">{formatPrice(product.price)}</p>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        )}
        <DialogFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('dashboard.close')}</Button>
          <Button onClick={onTalkToRep}>
            <Video className="h-4 w-4 mr-1" /> {t('dashboard.talk_to_rep')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BuyerBoothDetailModal;
